import React, { useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Route, Switch, Redirect } from 'react-router-dom';
import { FormattedMessage, IntlProvider } from 'react-intl';
import classNames from 'classnames';
import { get, isEmpty, isEqual } from 'lodash';

import resourcesConfig from '../../resources';
import asyncRoute from '../../helpers/hoc/asyncRoute';
import orderingRoutes from '../../routes/ordering';
import rewardRoutes from '../../routes/rewards';
import {
  extractWhitelabelSubdomain,
  getComponentVersionProps,
  isIosWebView,
} from '../../utils/helpers';
import { getErrorTitle, getErrorDescription } from '../../selectors';
import { apiResponseCodes, serverErrorCodes, storageTypes } from '../../constants/globals';
import { useTheme } from '../../utils/customHooks';
import { dispatchLogoutAction } from '../../actions/authentication';
import {
  fetchAppStartupInfoAction,
  dispatchContextualHome,
  clearApiErrorResponseAction,
} from '../../actions/common';
import { getAppInStartupPhase, setIsApiRequestOnHold } from '../../utils/helpers';
import { dispatchCallbackUrlAction, fetchRewardGuestDetailsAction } from '../../actions/rewards';
import { clearGuestPreviousOrderedItemsAction } from '../../actions/ordering';
import ThemeContext from '../../containers/ThemeContext';
import DefaultPage from '../Default';
import { isNativeWebView } from '../../utils/helpers';
import { checkExisitingRewardGuestApi } from '../../api/api';
import { getItem } from '../../utils/storage';
import { appResolutionConfig } from '../../containers/Rewards/selectors';
import DefaultRegularFont from '../../assets/fonts/OpenSans-Regular.ttf';
import DefaultSemiBoldFont from '../../assets/fonts/OpenSans-SemiBold.ttf';
import DefaultBoldFont from '../../assets/fonts/OpenSans-Bold.ttf';

import './Ordering.scss';

const NonExistentSubdomainFallback = asyncRoute(() =>
  import('../Fallback/NonExistentSubdomainFallback'),
);
const Welcome = asyncRoute(() => import('./Welcome'));
const Home = asyncRoute(() => import('./Home'));
const Cart = asyncRoute(() => import('./Cart'));
const MenuPage = asyncRoute(() => import('./MenuPage'));
const MenuCategoryPage = asyncRoute(() => import('./MenuCategoryPage'));
const MenuItemSearchPage = asyncRoute(() => import('./MenuItemSearchPage'));
const MenuOfferPage = asyncRoute(() => import('./MenuOfferPage'));
const MenuItemOfferPage = asyncRoute(() => import('./MenuItemOfferPage'));
const Coupons = asyncRoute(() => import('./Coupons'));
const Payment = asyncRoute(() => import('./Payment'));
const Confirmation = asyncRoute(() => import('./Confirmation'));
const Account = asyncRoute(() => import('./Account'));
const OrderListPage = asyncRoute(() => import('./OrderListPage'));
const Aboutus = asyncRoute(() => import('../Rewards/AboutUs'));
const RewardList = asyncRoute(() => import('./RewardList'));

const ExceptionComponent = asyncRoute(() => import('../../components/ExceptionComponent'));
const SessionExpiredPopover = asyncRoute(() =>
  import('../../containers/Rewards/components/SessionExpiredPopover'),
);

//Reward Page
const CouponDetails = asyncRoute(() => import('../Rewards/RewardDetails'));

const Ordering = ({
  actions,
  match,
  location,
  history,
  resources,
  themeData,
  contextualHome,
  fontFamilyUrl,
  boldFontURL,
  semiBoldFontURL,
  showWebElements,
  appStartupInfo,
  errorResponse,
  isWebApplicable,
  rewardGuestDetails,
  hasTiers,
  appResolutionContext,
  defaultWebRedirectionUrl,
  selectedTable,
  restaurant,
}) => {
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorContent, setErrorContent] = useState({});
  const [showNonExistentSubdomainFallback, setShowNonExistentSubdomainFallback] = useState(false);
  const isThemeApplied = useTheme('ordering-container', themeData);
  const [isMenuCategoryVisible, setIsMenuCategoryVisible] = useState(false);
  const [hideBottomFooter, setRemoveFooter] = useState(false);
  const { themeContext, nativeRouteName } = useContext(ThemeContext);
  const [showSessionExpired, setShowSessionExpired] = useState(false);

  useEffect(() => {
    const { pathname, search } = location;
    let activePathNames = pathname && pathname.split('/');

    if (!isNativeWebView && activePathNames.includes(rewardRoutes.rewards)) {
      const guestLoginData = getItem(storageTypes.localStorage, 'GuestLoginData');
      const guestId = get(guestLoginData, 'id', '');

      if (isEmpty(guestId)) {
        const callbackUrl = search ? `${pathname}${search}` : pathname;
        actions.dispatchCallbackUrl({ callbackUrl }).then(() => {
          history.push(
            `/${rewardRoutes.rewards}/${rewardRoutes.containers.authentication}/${rewardRoutes.authentication.signUp}`,
          );
        });
      }
    }
  }, []);

  useEffect(() => {
    const subDomain = extractWhitelabelSubdomain();
    const guestLoginData = getItem(storageTypes.localStorage, 'GuestLoginData');
    const guestId = get(guestLoginData, 'id', '');
    if (showWebElements && isEmpty(rewardGuestDetails)) {
      if (!isEmpty(guestId) && !isEmpty(subDomain)) {
        const params = `brandId=${subDomain}&guestId=${guestId}`;
        checkExisitingRewardGuestApi(params).then((response) => {
          if (apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
            const brandGuestId = get(response, 'data.id', '');
            if (!isEmpty(brandGuestId)) {
              actions.fetchRewardGuestDetails({ guestId: brandGuestId, depth: hasTiers });
            } else {
              // clear the authentication session state
              actions.dispatchLogoutAction();
            }
          }
        });
      }
    }
  }, [showWebElements]);

  useEffect(() => {
    let activePathNames = location.pathname && location.pathname.split('/');
    let activePathName = activePathNames[2];
    let redirectionUrl = '';
    if (!isEmpty(defaultWebRedirectionUrl)) {
      redirectionUrl = defaultWebRedirectionUrl;
    } else {
      redirectionUrl = `/${rewardRoutes.rewards}/${rewardRoutes.containers.getApp}`;
    }
    if (
      !isWebApplicable &&
      !isNativeWebView &&
      !isEqual(rewardRoutes.containers.getApp, activePathName) &&
      !isEqual(appResolutionContext, appResolutionConfig.webOrderFlow) &&
      !isEqual(appResolutionContext, appResolutionConfig.webOrderFlowWithLoyalty)
    ) {
      history.push(redirectionUrl);
    }
  }, [location, isWebApplicable]);

  useEffect(() => {
    const menuCategoriesVersion = get(
      getComponentVersionProps(themeData, 'menu-category-section'),
      'version',
    );
    if (!isEmpty(menuCategoriesVersion)) {
      setIsMenuCategoryVisible(true);
    }

    return () => {};
  }, [themeData]);

  useEffect(() => {
    if (
      !isEmpty(errorResponse) &&
      !isEqual(get(errorResponse, 'code'), serverErrorCodes.invalidToken)
    ) {
      const { code, message } = errorResponse;
      const draftErrorContent = {
        title: getErrorTitle({ source: resources, code }),
        description: getErrorDescription({ source: resources, code, message }),
      };
      setShowErrorPopup(true);
      setErrorContent(draftErrorContent);
    }
    if (
      !isEmpty(errorResponse) &&
      isEqual(get(errorResponse, 'code'), serverErrorCodes.invalidToken)
    ) {
      handleSessionExpiredErrorResponse(errorResponse);
    }
  }, [errorResponse]);

  useEffect(() => {
    const subDomain = extractWhitelabelSubdomain();
    if (subDomain && !isEqual(themeContext, rewardRoutes.rewards)) {
      // at rewards container we already have startup data
      const appStartupReqBody = {
        subdomain: subDomain,
        includeModules: ['onlineOrder', 'brand', 'resource'],
      };
      actions.fetchAppStartupInfo({ appStartupReqBody, appStartupInfo }).then((response) => {
        if (apiResponseCodes.SUCCESS.includes(get(response, 'status', ''))) {
          actions.dispatchContextualHome(`/${orderingRoutes.ordering}`);
        } else if (!isEqual(get(response, 'data.code'), serverErrorCodes.invalidToken)) {
          // incase if sub domain is not resolved
          setShowNonExistentSubdomainFallback(true);
        }
      });
    }

    // clear previous ordered items on un mount
    return () => {
      actions.clearGuestPreviousOrderedItems();
    };
  }, []);

  useEffect(() => {
    const defaultFontURL = DefaultRegularFont;
    const defaultFont = 'DefaultFont';
    const fontURL = !isEmpty(fontFamilyUrl) ? fontFamilyUrl : DefaultRegularFont;
    const RegularFont = 'RegularFont';
    const bFontURL = !isEmpty(boldFontURL) ? boldFontURL : DefaultBoldFont;
    const BoldFont = 'BoldFont';
    const sBFontURL = !isEmpty(semiBoldFontURL) ? semiBoldFontURL : DefaultSemiBoldFont;
    const SemiBoldFont = 'SemiBoldFont';
    var newStyle = document.createElement('style');
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
    font-family: ' +
          RegularFont +
          ";\
    src: url('" +
          fontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          BoldFont +
          ";\
  src: url('" +
          bFontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          SemiBoldFont +
          ";\
  src: url('" +
          sBFontURL +
          "') format('woff');\
}\
",
      ),
    );
    newStyle.appendChild(
      document.createTextNode(
        '\
@font-face {\
  font-family: ' +
          defaultFont +
          ";\
  src: url('" +
          defaultFontURL +
          "') format('woff');\
}\
",
      ),
    );
    document.head.appendChild(newStyle);
  }, [fontFamilyUrl, boldFontURL, semiBoldFontURL]);

  const onCloseExceptionComponent = () => {
    actions.clearApiErrorResponse().then(() => {
      setShowErrorPopup(false);
      setErrorContent({});
    });
  };

  const handleLogout = () => {
    localStorage.setItem('GuestLoginData', JSON.stringify({}));
    localStorage.setItem('AuthData', JSON.stringify({}));
    actions.dispatchLogoutAction();
    handleLogoutRedirection();
  };

  const handleLogoutRedirection = () => {
    const selectedTableScanCode = get(selectedTable, 'scanCode', '');
    const selectedRestaurantCode = get(restaurant, 'restaurantCode', '');
    if (!isEmpty(selectedTableScanCode)) {
      history.push(
        `/${rewardRoutes.containers.ordering}/${orderingRoutes.containers.menu}/${selectedTableScanCode}`,
        { showAuthenticationPopup: true },
      );
    } else if (!isEmpty(selectedRestaurantCode)) {
      history.push(
        `/${rewardRoutes.containers.ordering}/${orderingRoutes.containers.welcome}/${selectedRestaurantCode}`,
      );
    }
  };

  const handleSessionExpiredErrorResponse = (errorResponse) => {
    setIsApiRequestOnHold(false);
    const isAppInStartupPhase = getAppInStartupPhase();
    if (isAppInStartupPhase) {
      handleLogout(true);
      actions.clearApiErrorResponse();
    } else {
      setShowSessionExpired(true);
    }
  };

  const handleCloseSessionExpired = () => {
    const redirectToLoginScreen = true;
    setShowSessionExpired(false);
    actions.clearApiErrorResponse();
    handleLogout(redirectToLoginScreen);
  };

  const handleNonExistentSubdomainClick = () => {
    history.push(`/${orderingRoutes.scan}`);
  };

  const renderRoutes = () => (
    <IntlProvider messages={resources} locale="en-US" defaultLocale="en-US">
      {showNonExistentSubdomainFallback ? (
        <NonExistentSubdomainFallback
          resources={{
            title: (
              <FormattedMessage
                id="nonExistSubdomainFallbackTitle"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackTitle}
              />
            ),
            description: (
              <FormattedMessage
                id="nonExistSubdomainFallbackDescription"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackDescription}
              />
            ),
            atnBtn: (
              <FormattedMessage
                id="nonExistSubdomainFallbackCTA"
                defaultMessage={resourcesConfig.nonExistSubdomainFallbackCTA}
              />
            ),
          }}
          handleGoHome={() => handleNonExistentSubdomainClick()}
        />
      ) : (
        <Switch location={location}>
          <Route
            exact
            path={`${match.path}/${orderingRoutes.containers.welcome}/:restaurantAndTableId`}
            component={Welcome}
          />
          <Route exact path={`${match.path}`} component={Home} />
          <Route exact path={`${match.path}/${orderingRoutes.containers.cart}`} component={Cart} />
          <Route
            exact
            path={`${match.path}/${orderingRoutes.containers.menu}/:restaurantCode`}
            render={(props) => <MenuPage {...props} setRemoveFooter={setRemoveFooter} />}
          />
          {isMenuCategoryVisible && (
            <Route
              exact
              path={`${match.path}/${orderingRoutes.containers.category}/:categoryId`}
              component={MenuCategoryPage}
            />
          )}
          <Route
            exact
            path={`${match.path}/${orderingRoutes.containers.search}`}
            component={MenuItemSearchPage}
          />
          <Route
            exact
            path={`${match.path}/${orderingRoutes.containers.menuOffer}/:couponCode`}
            component={MenuOfferPage}
          />
          <Route
            exact
            path={`${match.path}/${orderingRoutes.containers.menuItemOffer}/:couponCode`}
            component={MenuItemOfferPage}
          />
          <Route
            exact
            path={`${match.path}/${orderingRoutes.containers.orders}`}
            component={OrderListPage}
          />
          <Route
            exact
            path={`${match.path}/${orderingRoutes.containers.account}`}
            component={Account}
          />
          <Route
            exact
            path={`${match.path}/${orderingRoutes.containers.offers}`}
            component={Coupons}
          />
          <Route
            exact
            path={`${match.path}/${orderingRoutes.containers.offers}/:couponCode`}
            component={CouponDetails}
          />
          <Route
            exact
            path={`${match.path}/${orderingRoutes.containers.aboutOutlet}`}
            component={Aboutus}
          />
          <Route
            exact
            path={`${match.path}/${orderingRoutes.containers.payment}`}
            component={Payment}
          />
          <Route
            exact
            path={`${match.path}/${orderingRoutes.containers.confirmation}/:orderId`}
            component={Confirmation}
          />
          <Route
            exact
            path={`${match.path}`}
            render={() => <Redirect to={`${match.path}/${orderingRoutes.containers.scan}`} />}
          />
          <Route
            exact
            path={`${match.path}/${orderingRoutes.containers.rewardList}`}
            component={RewardList}
          />
          {/* Default route */}
          {isEmpty(contextualHome) ? (
            <Route path="/" component={DefaultPage} />
          ) : (
            <Redirect to={contextualHome} />
          )}
        </Switch>
      )}
    </IntlProvider>
  );

  return (
    <div
      className={classNames('ordering', {
        'in-app-style': isIosWebView,
        hideBottomFooter: hideBottomFooter,
      })}
      id="ordering-container"
    >
      <ThemeContext.Provider
        value={{
          themeContext: rewardRoutes.rewards,
          showWebElements: showWebElements,
          nativeRouteName: nativeRouteName,
        }}
      >
        {isThemeApplied && (
          <>
            {renderRoutes()}
            {showWebElements && showErrorPopup && (
              <ExceptionComponent
                pageType="HalfSize"
                openSegmentPopup={showErrorPopup}
                dismissSegmentPopup={onCloseExceptionComponent}
                title={get(errorContent, 'title')}
                description={get(errorContent, 'description')}
              />
            )}
            {showSessionExpired && (
              <SessionExpiredPopover
                open={showSessionExpired}
                onCTAClick={handleCloseSessionExpired}
              />
            )}
          </>
        )}
      </ThemeContext.Provider>
    </div>
  );
};

const mapStateToProps = (state) => ({
  contextualHome: get(state, 'common.contextualHome', ''),
  resources: get(state, 'common.resources', resourcesConfig),
  themeData: get(state, 'common.resources.themeData', {}),
  fontFamilyUrl: get(state, 'common.resources.themeData.fontFamilyUrl', ''),
  defaultFontFamily: get(state, 'common.resources.themeData.fontFamilyName', ''),
  restaurant: get(state, 'ordering.restaurant', ''),
  selectedTable: get(state, 'ordering.selectedTable', ''),
  appStartupInfo: get(state, 'common.appStartupInfo', {}),
  errorResponse: get(state, 'common.errorResponse', {}),
  isWebApplicable: get(state, 'common.resources.isWebApplicable', true),
  rewardGuestDetails: get(state, 'rewards.rewardGuestDetails', {}),
  hasTiers: get(state, 'common.appStartupInfo.rewardProgram.hasTiers', false),
  semiBoldFontURL: get(state, 'common.resources.themeData.semiBoldFontURL', ''),
  boldFontURL: get(state, 'common.resources.themeData.boldFontURL', ''),
  appResolutionContext: get(state, 'common.appResolutionContext', ''),
  defaultWebRedirectionUrl: get(state, 'common.resources.defaultWebRedirectionUrl', ''),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchAppStartupInfo: fetchAppStartupInfoAction,
      clearGuestPreviousOrderedItems: clearGuestPreviousOrderedItemsAction,
      dispatchContextualHome,
      clearApiErrorResponse: clearApiErrorResponseAction,
      dispatchCallbackUrl: dispatchCallbackUrlAction,
      fetchRewardGuestDetails: fetchRewardGuestDetailsAction,
      dispatchLogoutAction,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Ordering);
